package com.milta.feature.team.data.repository

import com.milta.core.cache.LocalCacheStore
import com.milta.core.network.ApiException
import com.milta.feature.team.data.remote.TeamApi
import com.milta.feature.team.data.remote.dto.DriverDto
import com.milta.feature.team.data.remote.dto.TeamMembershipDto
import com.milta.feature.team.data.remote.dto.toDomain
import com.milta.feature.team.domain.entity.Driver
import com.milta.feature.team.domain.entity.Enterprise
import com.milta.feature.team.domain.entity.TeamMembership
import com.milta.feature.team.domain.repository.TeamRepository
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

private const val TEAM_CACHE_KEY = "cache:team"
private const val DRIVERS_CACHE_KEY_PREFIX = "cache:drivers:"

class TeamRepositoryImpl(
    private val api: TeamApi,
    private val cacheStore: LocalCacheStore,
) : TeamRepository {
    // Mode hors ligne — meme cache-aside que RouteRepositoryImpl.getMyRoute.
    override suspend fun getMyTeam(): Result<List<TeamMembership>> = runCatching {
        api.getMyTeam()
    }.onSuccess {
        cacheStore.putString(TEAM_CACHE_KEY, Json.encodeToString(it))
    }.recoverCatching { e ->
        if (e is ApiException) throw e
        val cached = cacheStore.getString(TEAM_CACHE_KEY) ?: throw e
        Json.decodeFromString<List<TeamMembershipDto>>(cached)
    }.map { list -> list.map { it.toDomain() } }

    // Mode hors ligne — meme cache-aside, une entree par entreprise (Creer une tournee / choix du
    // chauffeur).
    override suspend fun getDrivers(enterpriseId: String): Result<List<Driver>> {
        val cacheKey = "$DRIVERS_CACHE_KEY_PREFIX$enterpriseId"
        return runCatching { api.getDrivers(enterpriseId) }
            .onSuccess { cacheStore.putString(cacheKey, Json.encodeToString(it)) }
            .recoverCatching { e ->
                if (e is ApiException) throw e
                val cached = cacheStore.getString(cacheKey) ?: throw e
                Json.decodeFromString<List<DriverDto>>(cached)
            }
            .map { list -> list.map { it.toDomain() } }
    }

    // MIL-49 — no offline fallback: linking requires a live round-trip (uniqueness/consumption of
    // the code is enforced server-side), unlike the read-only getMyTeam/getDrivers above.
    // Appends the newly linked driver to the cached drivers list (if one exists) so a subsequent
    // offline getDrivers() call doesn't serve the pre-link snapshot — mirrors removeDriver's
    // cache upkeep below, but appends instead of invalidating since we already have the linked
    // Driver at hand from the server response.
    override suspend fun linkDriver(enterpriseId: String, code: String): Result<Driver> = runCatching {
        api.linkDriver(enterpriseId, code)
    }.onSuccess { driverDto ->
        val cacheKey = "$DRIVERS_CACHE_KEY_PREFIX$enterpriseId"
        val cached = cacheStore.getString(cacheKey) ?: return@onSuccess
        val drivers = runCatching { Json.decodeFromString<List<DriverDto>>(cached) }.getOrNull() ?: return@onSuccess
        cacheStore.putString(cacheKey, Json.encodeToString(drivers + driverDto))
    }.map { it.toDomain() }

    // Invalidates the cached drivers list for this enterprise so the next getDrivers() call
    // (returning to the team screen) re-fetches instead of serving the pre-removal cache offline.
    override suspend fun removeDriver(enterpriseId: String, driverId: String): Result<Unit> = runCatching {
        api.removeDriver(enterpriseId, driverId)
    }.onSuccess {
        cacheStore.remove("$DRIVERS_CACHE_KEY_PREFIX$enterpriseId")
    }

    // MIL-53 — no offline fallback: the Organisation profile is a live, low-frequency read (Settings
    // page), unlike getMyTeam/getDrivers which back screens visited far more often while offline.
    override suspend fun getMyEnterprise(): Result<Enterprise> = runCatching {
        api.getMyEnterprise().toDomain()
    }

    override suspend fun deleteMyEnterprise(): Result<Unit> = runCatching {
        api.deleteMyEnterprise()
    }.onSuccess {
        cacheStore.remove(TEAM_CACHE_KEY)
    }
}
